<template>
  <v-col>
    <QuoteFormHeader
      :quoteId="$route.params.quote_id"
      :peopleRanges="data.peopleRanges"
      :periodOptions="periodSwitcherOptions"
      :defaultPeriodKey="selectedPeriodPreview"
      :isSubmitDisabled="!isQuoteValid"
      :onGenerateToBooking="submitAndGenerateBooking"
      :onExportOverviewToExcel="submitAndExportOverviewToExcel"
      :onExportDetailToExcel="submitAndExportDetailToExcel"
      :onOverviewOptions="overviewOptions"
      :onProgramContent="programContent"
      :onDiscountMargin="discountMargin"
      :onNotes="notes"
      :onDelete="deleteQuote"
      :onSubmit="validateSupplierSettingUpdated"
      :onDuplicate="submitAsNewQuote"
    />
    <v-col>
      <!-- Overview Option -->
      <OverviewOptionsDialog
        v-model="isOverviewOptionDialogShowed"
        :data="data"
        @update="(v) => updateOverviewOptions(v)"
      />

      <!-- Program content -->
      <ProgramContentDialog
        v-model="isProgramContentDialogShowed"
        :data="data"
        @update="(v) => updateProgramContent(v)"
      />

      <!-- Discount Margin -->
      <DiscountMarginDialog
        v-model="isDiscountMarginDialogShowed"
        :data="data"
        @update="(v) => updateDiscountMargin(v)"
      />

      <!-- Note -->
      <NotesDialog v-model="isNotesDialogShowed" :data="data" @update="(v) => updateNotes(v)" />

      <v-alert v-if="showValidationErrors && validationIssues.length" type="error" dense outlined class="mb-4">
        <div class="font-weight-medium mb-2">Please fix the following before saving:</div>
        <ul class="quote-validation-list mb-0 pl-4">
          <li v-for="(issue, index) in validationIssues" :key="index">{{ issue.message }}</li>
        </ul>
      </v-alert>

      <!-- General Section -->
      <div id="quote-section-general">
        <QuoteGeneral
          v-model="data"
          :supplierProductData="supplierProductData"
          :selectedPeriodPreview="selectedPeriodPreview"
          @update:selectedPeriodPreview="updateSelectedPeriodPreview"
        />
      </div>

      <a-card v-if="periodSwitcherOptions.length > 1" class="period-price-banner">
        <div class="period-price-banner__content">
          <span class="period-price-banner__label">Prices shown below are for</span>
          <v-chip small color="primary" text-color="white" class="period-price-banner__active">
            {{ activePreviewLabel }}
          </v-chip>
        </div>
        <div v-if="isComparisonPreviewMode" class="period-price-banner__hint caption">
          Custom costs and margin can be edited independently for this period. Program structure stays locked to
          {{ getPeriodDisplayLabel(data.periodName, data.period || {}) }}.
        </div>
      </a-card>

      <!-- Costs Section -->
      <div id="quote-section-program">
        <QuoteCosts
          v-model="displayData"
          :supplierProductData="supplierProductData"
          :supplierDropdownState="supplierDropdownState"
          :readonly="isComparisonPreviewMode"
          :allowPriceEdits="isComparisonPreviewMode"
          @onSupplierChanged="getProductsBySupplierId"
          @onProductFocus="fetchProductListBySupplierId"
          @onSupplierFocus="onSupplierFocus"
          @onSupplierSearch="onSupplierSearch"
          @onSupplierLoadMore="onSupplierLoadMore"
          @onProductTypeChanged="onCostProductTypeChanged"
          @price-override-changed="onComparisonPriceOverrideChanged"
        />
      </div>

      <!-- Logistics Costs Section -->
      <div id="quote-section-logistics">
        <QuoteLogisticsCosts
          v-model="displayData"
          :supplierProductData="supplierProductData"
          :supplierDropdownState="supplierDropdownState"
          :readonly="isComparisonPreviewMode"
          :allowPriceEdits="isComparisonPreviewMode"
          @onSupplierChanged="getProductsBySupplierId"
          @onProductFocus="fetchProductListBySupplierId"
          @onSupplierFocus="onSupplierFocus"
          @onSupplierSearch="onSupplierSearch"
          @onSupplierLoadMore="onSupplierLoadMore"
          @openSupplierSettingDialog="openSupplierSettingDialog"
          @onProductTypeChanged="onLogisticsCostProductTypeChanged"
          @price-override-changed="onComparisonPriceOverrideChanged"
        />
      </div>

      <!-- Total Costs Section -->
      <div id="quote-section-total-costs">
        <QuoteTotalCosts v-model="displayData" :readonly="isComparisonPreviewMode" />
      </div>

      <!-- Margin Section -->
      <div id="quote-section-margin">
        <QuoteMargin
          :key="selectedPeriodPreview"
          v-model="marginFormData"
          :supplierProductData="supplierProductData"
          @supplement-overrides-changed="updateSupplementOverrides"
        />
      </div>
    </v-col>

    <!-- Supplier Setting Dialog -->
    <SupplierSettingDialog
      v-model="isSupplierSettingDialogShowed"
      :isSubmitting="isSubmitting"
      :data="data"
      @update="(v) => updateData(v)"
    />
  </v-col>
</template>

<script>
import quoteUtils from "@/modules/quotes/components/utils";
import {
  getQuoteValidationIssues,
  isQuoteValid as checkQuoteValid,
  formatQuoteValidationMessage,
} from "@/modules/quotes/components/quoteValidation";
import { mapQuoteResponseToFormData, mapFormDataToBody } from "@/modules/quotes/components/mapper";
import {
  QuoteFormHeader,
  QuoteGeneral,
  QuoteCosts,
  QuoteLogisticsCosts,
  QuoteTotalCosts,
  QuoteMargin,
  DiscountMarginDialog,
  NotesDialog,
  OverviewOptionsDialog,
  ProgramContentDialog,
  SupplierSettingDialog,
} from "@/modules/quotes";
export default {
  components: {
    QuoteFormHeader,
    QuoteGeneral,
    QuoteCosts,
    QuoteLogisticsCosts,
    QuoteTotalCosts,
    QuoteMargin,
    DiscountMarginDialog,
    NotesDialog,
    OverviewOptionsDialog,
    ProgramContentDialog,
    SupplierSettingDialog,
  },
  props: {
    quote: {
      type: Object,
      default: () => null,
    },
    supplierProductData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loadedProductSupplierIds: [],
      tempCosts: [],
      isDiscountMarginDialogShowed: false,
      isNotesDialogShowed: false,
      isOverviewOptionDialogShowed: false,
      isProgramContentDialogShowed: false,
      isSupplierSettingDialogShowed: false,
      isSupplierSettingUpdated: false,
      isSubmitting: false,
      supplierDropdownState: {},
      supplierSearchTimers: {},
      selectedPeriodPreview: "primary",
      showValidationErrors: false,
      validationIssues: [],
      comparisonMarginPreview: null,
      comparisonPreviewData: null,
      isSyncingComparisonCostOverrides: false,
      isRefreshingComparisonPreview: false,
    };
  },
  computed: {
    data: {
      get() {
        return this.quote;
      },
      set(val) {
        this.$emit("update:quote", val);
      },
    },
    isQuoteValid() {
      return checkQuoteValid(this.data);
    },
    maxRangeIndex() {
      return this.data.peopleRanges.reduce((maxIndex, current, index, array) => {
        return current.min > array[maxIndex].min ? index : maxIndex;
      }, 0);
    },
    perPax() {
      return this.data.margin.perPax[this.maxRangeIndex] ?? 0;
    },
    marginPercentage() {
      return this.data.margin.status === "fixed"
        ? this.data.margin.percentFixed
        : (this.data.margin.percentDynamic[this.maxRangeIndex] ?? 0);
    },
    periodSwitcherOptions() {
      const primaryPeriod = this.data.period || {};
      const comparisonPeriods = (this.data.comparisonPeriods || []).slice(
        0,
        quoteUtils.MAX_COMPARISON_PERIODS
      );

      return [
        {
          value: "primary",
          label: this.getPeriodDisplayLabel(this.data.periodName, primaryPeriod),
        },
        ...comparisonPeriods.map((comparisonPeriod, index) => ({
          value: `comparison-${index}`,
          label: this.getPeriodDisplayLabel(comparisonPeriod?.periodName, comparisonPeriod?.period || {}),
        })),
      ];
    },
    selectedPreviewPeriodConfig() {
      if (this.selectedPeriodPreview === "primary") return null;
      const prefix = "comparison-";
      if (!this.selectedPeriodPreview.startsWith(prefix)) return null;

      const index = Number(this.selectedPeriodPreview.slice(prefix.length));
      if (Number.isNaN(index)) return null;

      return this.data.comparisonPeriods?.[index] || null;
    },
    isComparisonPreviewMode() {
      return !!this.selectedPreviewPeriodConfig;
    },
    activePreviewLabel() {
      if (!this.selectedPreviewPeriodConfig) return this.getPeriodDisplayLabel(this.data.periodName, this.data.period || {});
      return this.getPeriodDisplayLabel(
        this.selectedPreviewPeriodConfig.periodName,
        this.selectedPreviewPeriodConfig.period || {}
      );
    },
    displayData: {
      get() {
        if (!this.selectedPreviewPeriodConfig) return this.data;
        return this.comparisonPreviewData || this.buildPreviewQuoteData(this.selectedPreviewPeriodConfig);
      },
      set(val) {
        if (!this.isComparisonPreviewMode) {
          this.data = val;
          return;
        }

        if (this.comparisonPreviewData) {
          this.comparisonPreviewData.costs = val.costs;
          this.comparisonPreviewData.logisticsCosts = val.logisticsCosts;
        }
        this.syncCostOverridesFromPreview(val);
      },
    },
    selectedComparisonPeriodIndex() {
      if (!this.isComparisonPreviewMode) return null;
      const prefix = "comparison-";
      const index = Number(String(this.selectedPeriodPreview).replace(prefix, ""));
      return Number.isNaN(index) ? null : index;
    },
    marginFormData: {
      get() {
        if (!this.isComparisonPreviewMode) return this.data;
        return this.comparisonMarginPreview || this.comparisonPreviewData || this.data;
      },
      set(val) {
        if (!this.isComparisonPreviewMode) {
          this.data = val;
          return;
        }

        const index = this.selectedComparisonPeriodIndex;
        if (index == null || !this.data.comparisonPeriods?.[index] || !val?.margin) return;

        this.$set(this.data.comparisonPeriods[index], "margin", val.margin);
        if (this.comparisonMarginPreview) {
          this.comparisonMarginPreview.margin = this.data.comparisonPeriods[index].margin;
        }
        if (this.comparisonPreviewData) {
          this.comparisonPreviewData.margin = this.data.comparisonPeriods[index].margin;
        }
      },
    },
  },
  watch: {
    selectedPeriodPreview: {
      immediate: true,
      handler() {
        this.refreshComparisonMarginPreview();
      },
    },
    "data.costs": {
      deep: true,
      handler() {
        if (this.isComparisonPreviewMode && !this.isSyncingComparisonCostOverrides) {
          this.refreshComparisonMarginPreview();
        }
      },
    },
    "data.logisticsCosts": {
      deep: true,
      handler() {
        if (this.isComparisonPreviewMode && !this.isSyncingComparisonCostOverrides) {
          this.refreshComparisonMarginPreview();
        }
      },
    },
    "data.peopleRanges": {
      deep: true,
      handler() {
        if (this.isComparisonPreviewMode) this.refreshComparisonMarginPreview();
      },
    },
    data: {
      deep: true,
      handler() {
        if (this.showValidationErrors && this.isQuoteValid) {
          this.showValidationErrors = false;
          this.validationIssues = [];
          this.$store.commit("QuoteStore/SET_VALIDATION_ISSUES", []);
        }
      },
    },
    "$store.state.QuoteStore.validationIssues": {
      handler(issues) {
        if (!issues?.length) return;
        this.validationIssues = issues;
        this.showValidationErrors = true;
        this.$nextTick(() => this.scrollToFirstValidationIssue(issues[0]));
      },
    },
    "data.comparisonPeriods": {
      handler(comparisonPeriods) {
        if (this.selectedPeriodPreview === "primary") return;

        const prefix = "comparison-";
        const index = Number(String(this.selectedPeriodPreview).replace(prefix, ""));
        const stillExists =
          !Number.isNaN(index) && Array.isArray(comparisonPeriods) && index >= 0 && index < comparisonPeriods.length;

        if (!stillExists) {
          this.selectedPeriodPreview = "primary";
          return;
        }

        if (!this.isSyncingComparisonCostOverrides && !this.isRefreshingComparisonPreview) {
          this.refreshComparisonMarginPreview();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    cloneValue(value) {
      return JSON.parse(JSON.stringify(value));
    },
    getPeriodDisplayLabel(periodName, period) {
      if (periodName) return periodName;
      return quoteUtils.getFrenchArrivalLabel(period) || "Unnamed period";
    },
    updateSelectedPeriodPreview(value) {
      this.selectedPeriodPreview = value || "primary";
    },
    onComparisonPriceOverrideChanged() {
      if (!this.isComparisonPreviewMode || !this.comparisonPreviewData) return;
      this.syncCostOverridesFromPreview(this.comparisonPreviewData);
    },
    refreshComparisonMarginPreview() {
      if (this.isRefreshingComparisonPreview) return;

      const index = this.selectedComparisonPeriodIndex;
      if (index == null || !this.data.comparisonPeriods?.[index]) {
        this.comparisonMarginPreview = null;
        this.comparisonPreviewData = null;
        return;
      }

      this.isRefreshingComparisonPreview = true;
      try {
        this.ensureComparisonPeriodMargin(index);
        this.ensureComparisonPeriodCostOverrides(index);

        const previewData = this.buildPreviewQuoteData(this.data.comparisonPeriods[index]);
        // Keep a stable object with a live margin reference so margin edits don't rebuild costs.
        previewData.margin = this.data.comparisonPeriods[index].margin;
        previewData.discountMargin = this.data.discountMargin;
        previewData.supplementTripleOverride =
          this.data.comparisonPeriods[index].supplementTripleOverride ?? null;
        previewData.supplementQuadOverride =
          this.data.comparisonPeriods[index].supplementQuadOverride ?? null;
        this.comparisonPreviewData = previewData;
        this.comparisonMarginPreview = previewData;
      } finally {
        this.$nextTick(() => {
          this.isRefreshingComparisonPreview = false;
        });
      }
    },
    ensureComparisonPeriodMargin(index) {
      const comparisonPeriod = this.data.comparisonPeriods?.[index];
      if (!comparisonPeriod) return;

      if (!comparisonPeriod.margin) {
        this.$set(this.data.comparisonPeriods[index], "margin", this.clonePrimaryMargin());
        return;
      }

      const margin = comparisonPeriod.margin;
      if (!Array.isArray(margin.percentDynamic)) this.$set(margin, "percentDynamic", []);
      if (!Array.isArray(margin.perPax)) this.$set(margin, "perPax", []);
      if (!Array.isArray(margin.perGroup)) this.$set(margin, "perGroup", []);
      if (!margin.status) this.$set(margin, "status", "fixed");
      if (margin.percentFixed == null) this.$set(margin, "percentFixed", 0);
    },
    ensureComparisonPeriodCostOverrides(index) {
      const comparisonPeriod = this.data.comparisonPeriods?.[index];
      if (!comparisonPeriod) return;

      if (!comparisonPeriod.costOverrides || typeof comparisonPeriod.costOverrides !== "object") {
        this.$set(this.data.comparisonPeriods[index], "costOverrides", {});
      }
      if (!comparisonPeriod.logisticsCostOverrides || typeof comparisonPeriod.logisticsCostOverrides !== "object") {
        this.$set(this.data.comparisonPeriods[index], "logisticsCostOverrides", {});
      }

      const costOverrides = this.data.comparisonPeriods[index].costOverrides;
      const logisticsCostOverrides = this.data.comparisonPeriods[index].logisticsCostOverrides;

      (this.data.costs || []).forEach((day) => {
        (day.items || []).forEach((item) => {
          if (!item?.uuid || !item.isProductEditableCost || costOverrides[item.uuid]) return;
          this.$set(costOverrides, item.uuid, {
            productCost: this.cloneValue(item.productCost),
            isProductEditableCost: true,
          });
        });
      });

      (this.data.logisticsCosts || []).forEach((item) => {
        if (!item?.uuid || !item.isProductEditableCost || logisticsCostOverrides[item.uuid]) return;
        this.$set(logisticsCostOverrides, item.uuid, {
          productCost: this.cloneValue(item.productCost),
          isProductEditableCost: true,
        });
      });
    },
    findPrimaryCostItemByUuid(uuid) {
      if (!uuid) return null;
      for (const day of this.data.costs || []) {
        const item = (day.items || []).find((costItem) => costItem.uuid === uuid);
        if (item) return item;
      }
      return null;
    },
    findPrimaryLogisticsCostByUuid(uuid) {
      if (!uuid) return null;
      return (this.data.logisticsCosts || []).find((item) => item.uuid === uuid) || null;
    },
    syncCostOverridesFromPreview(previewData) {
      const index = this.selectedComparisonPeriodIndex;
      if (index == null || !this.data.comparisonPeriods?.[index] || !previewData) return;

      this.isSyncingComparisonCostOverrides = true;
      try {
        this.ensureComparisonPeriodCostOverrides(index);
        const comparisonPeriod = this.data.comparisonPeriods[index];
        const costOverrides = { ...comparisonPeriod.costOverrides };
        const logisticsCostOverrides = { ...comparisonPeriod.logisticsCostOverrides };

        (previewData.costs || []).forEach((day) => {
          (day.items || []).forEach((item) => {
            if (!item?.uuid) return;
            const primaryItem = this.findPrimaryCostItemByUuid(item.uuid);
            this.applyPreviewItemToOverrides(costOverrides, item, primaryItem);
          });
        });

        (previewData.logisticsCosts || []).forEach((item) => {
          if (!item?.uuid) return;
          const primaryItem = this.findPrimaryLogisticsCostByUuid(item.uuid);
          this.applyPreviewItemToOverrides(logisticsCostOverrides, item, primaryItem);
        });

        this.$set(this.data.comparisonPeriods[index], "costOverrides", costOverrides);
        this.$set(this.data.comparisonPeriods[index], "logisticsCostOverrides", logisticsCostOverrides);
      } finally {
        this.$nextTick(() => {
          this.isSyncingComparisonCostOverrides = false;
        });
      }
    },
    applyPreviewItemToOverrides(overrides, previewItem, primaryItem) {
      const uuid = previewItem.uuid;
      const primaryIsEditable = !!primaryItem?.isProductEditableCost;

      if (previewItem.isProductEditableCost) {
        overrides[uuid] = {
          productCost: previewItem.productCost,
          isProductEditableCost: true,
        };
        return;
      }

      if (primaryIsEditable) {
        // Period-specific lock while primary remains custom.
        overrides[uuid] = {
          productCost: previewItem.productCost,
          isProductEditableCost: false,
        };
        return;
      }

      if (overrides[uuid]) {
        delete overrides[uuid];
      }
    },
    clonePrimaryMargin() {
      const source = this.data.margin || {};
      return {
        status: source.status || "fixed",
        percentFixed: source.percentFixed ?? 0,
        percentDynamic: [...(source.percentDynamic || [])],
        perPax: [...(source.perPax || [])],
        perGroup: [...(source.perGroup || [])],
      };
    },
    buildPreviewQuoteData(periodConfig) {
      const previewData = this.cloneValue(this.data);
      const previewPeriod = this.cloneValue(periodConfig?.period || {});

      previewData.periodName = periodConfig?.periodName || previewData.periodName;
      previewData.period = previewPeriod;
      previewData.cityStart = periodConfig?.cityStart ?? previewData.cityStart;
      previewData.cityEnd = periodConfig?.cityEnd ?? previewData.cityEnd;
      previewData.costs = this.buildPreviewCosts(previewPeriod, periodConfig);
      previewData.logisticsCosts = this.buildPreviewLogisticsCosts(previewPeriod, periodConfig);

      return previewData;
    },
    buildPreviewCosts(period, periodConfig) {
      const previewCosts = this.cloneValue(this.data.costs || []);

      previewCosts.forEach((day, dayIndex) => {
        day.items = (day.items || []).map((item) =>
          this.buildPreviewProgramItem(item, period, dayIndex, previewCosts, periodConfig)
        );
      });

      return previewCosts;
    },
    buildPreviewProgramItem(item, period, dayIndex, previewCosts, periodConfig) {
      const previewItem = this.cloneValue(item);
      const override = periodConfig?.costOverrides?.[item.uuid];

      if (override?.isProductEditableCost) {
        previewItem.productCost = override.productCost;
        previewItem.isProductEditableCost = true;
        return previewItem;
      }

      if (override && override.isProductEditableCost === false) {
        previewItem.isProductEditableCost = false;
      } else if (previewItem.isProductEditableCost) {
        return previewItem;
      }

      const supplierId = previewItem.supplier?.value;
      const productId = previewItem.product?.value;
      const product = this.supplierProductData[supplierId]?.products?.[productId];
      if (!supplierId || !productId || !product) return previewItem;

      const previousDayHotelId = this.getPreviewPreviousDayHotelId(previewCosts, dayIndex);
      const productCost = quoteUtils.getProductCost(
        this.$store.getters["AccountStore/environment"],
        this.supplierProductData,
        product,
        period.startYear,
        period,
        dayIndex,
        previousDayHotelId
      );

      previewItem.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };
      previewItem.productType = product.product_type;
      previewItem.costType = productCost.costType;
      previewItem.productProgramDescription = quoteUtils.getProductProgramDescription(product, period.startYear);
      previewItem.productProgramTitle = quoteUtils.getProductProgramTitle(product, period.startYear);
      previewItem.programSupplierName = quoteUtils.getProgramSupplierName(
        this.supplierProductData[supplierId]?.supplier
      );
      quoteUtils.applyShowInProgramForProductType(previewItem);

      return previewItem;
    },
    buildPreviewLogisticsCosts(period, periodConfig) {
      return this.cloneValue(this.data.logisticsCosts || []).map((item) => {
        const override = periodConfig?.logisticsCostOverrides?.[item.uuid];

        if (override?.isProductEditableCost) {
          return {
            ...item,
            productCost: override.productCost,
            isProductEditableCost: true,
          };
        }

        let previewItem = { ...item };
        if (override && override.isProductEditableCost === false) {
          previewItem = { ...previewItem, isProductEditableCost: false };
        } else if (item.isProductEditableCost) {
          return item;
        }

        const supplierId = previewItem.supplier?.value;
        const productId = previewItem.product?.value;
        const product = this.supplierProductData[supplierId]?.products?.[productId];
        if (!supplierId || !productId || !product) return previewItem;

        const productCost = quoteUtils.getProductCost(
          this.$store.getters["AccountStore/environment"],
          this.supplierProductData,
          product,
          period.startYear,
          period,
          null,
          null
        );

        return quoteUtils.applyShowInProgramForProductType({
          ...previewItem,
          productCost: {
            type: productCost.pricingType,
            currency: productCost.currency,
            value: productCost.value,
            valueIncludesPorterage: productCost.valueIncludesPorterage,
            porterage: productCost.porterage,
            confirm: productCost.confirm,
            rateKind: productCost.rateKind,
          },
          productType: product.product_type,
        });
      });
    },
    getPreviewPreviousDayHotelId(costs, dayIndex) {
      if (dayIndex === 0) return null;

      const previousDayItems = costs[dayIndex - 1]?.items || [];
      const room = previousDayItems.find((item) => item.productType === "ROOM");
      return room?.supplier?.value || null;
    },
    updateOverviewOptions(overview) {
      this.data.overviewOptions.included = overview.overviewIncluded;
      this.data.overviewOptions.excluded = overview.overviewExcluded;
      this.data.overviewOptions.disclaimer = overview.overviewDisclaimer;

      this.partialUpdateQuote({
        overview_included: overview.overviewIncluded,
        overview_excluded: overview.overviewExcluded,
        overview_disclaimer: overview.overviewDisclaimer,
      });
    },
    updateProgramContent(payload) {
      this.data.program = {
        ...(this.data.program || {}),
        introduction: payload.introduction,
        supernatureSuccesses: payload.supernatureSuccesses,
      };

      const workingEnv = this.$store.getters["AccountStore/environment"];
      const body = mapFormDataToBody(this.data, workingEnv);
      this.partialUpdateQuote({
        program: body.quote.program,
      });
    },
    updateDiscountMargin(discountMargin) {
      this.data.discountMargin = discountMargin;
      this.partialUpdateQuote({ discount_margin: discountMargin });
    },
    updateSupplementOverrides({ supplementTripleOverride, supplementQuadOverride }) {
      const triple =
        supplementTripleOverride != null && supplementTripleOverride !== ""
          ? Number(supplementTripleOverride)
          : null;
      const quad =
        supplementQuadOverride != null && supplementQuadOverride !== ""
          ? Number(supplementQuadOverride)
          : null;

      if (this.isComparisonPreviewMode) {
        const index = this.selectedComparisonPeriodIndex;
        if (index == null || !this.data.comparisonPeriods?.[index]) return;

        this.$set(this.data.comparisonPeriods[index], "supplementTripleOverride", triple);
        this.$set(this.data.comparisonPeriods[index], "supplementQuadOverride", quad);

        if (this.comparisonPreviewData) {
          this.comparisonPreviewData.supplementTripleOverride = triple;
          this.comparisonPreviewData.supplementQuadOverride = quad;
        }
        if (this.comparisonMarginPreview) {
          this.comparisonMarginPreview.supplementTripleOverride = triple;
          this.comparisonMarginPreview.supplementQuadOverride = quad;
        }

        const workingEnv = this.$store.getters["AccountStore/environment"];
        const body = mapFormDataToBody(this.data, workingEnv);
        this.partialUpdateQuote({
          comparison_periods: body.quote.comparison_periods,
        });
        return;
      }

      this.data.supplementTripleOverride = triple;
      this.data.supplementQuadOverride = quad;

      this.partialUpdateQuote({
        supplement_triple_override: this.data.supplementTripleOverride,
        supplement_quad_override: this.data.supplementQuadOverride,
      });
    },
    updateNotes(notes) {
      this.data.notes = notes;
      this.partialUpdateQuote({ notes: notes });
    },
    getProductsBySupplierId(supplierId, productType) {
      this.$store.dispatch("ProductStore/getSupplierProductList", supplierId).then((data) => {
        let productsToProcess = [...data];
        if (productType) {
          productsToProcess.sort((a, b) => {
            const aIsMatch = a.product_type === productType;
            const bIsMatch = b.product_type === productType;
            return Number(bIsMatch) - Number(aIsMatch);
          });
        }
        const products = {};
        for (const product of productsToProcess) {
          products[product.id] = product;
        }

        const updatedSupplierProductData = { ...this.supplierProductData };
        updatedSupplierProductData[supplierId] = {
          ...updatedSupplierProductData[supplierId],
          products: products,
        };
        this.$emit("update:supplier-product-data", updatedSupplierProductData);
      });
    },
    getDefaultSupplierDropdownState() {
      return {
        supplierIds: [],
        keyword: "",
        type: null,
        source: "program",
        page: 0,
        limit: 20,
        hasMore: false,
        loading: false,
        mode: "initial",
        requestKey: null,
      };
    },
    updateSupplierDropdownState(contextKey, patch) {
      const current = this.supplierDropdownState[contextKey] || this.getDefaultSupplierDropdownState();
      this.$set(this.supplierDropdownState, contextKey, {
        ...current,
        ...patch,
      });
    },
    clearSupplierSearchTimer(contextKey) {
      const timerId = this.supplierSearchTimers[contextKey];
      if (!timerId) return;

      clearTimeout(timerId);
      this.$delete(this.supplierSearchTimers, contextKey);
    },
    getSupplierTypeFilter(productType, source) {
      const supplierType = quoteUtils.getSupplierTypeByProductType(productType);
      if (supplierType) return supplierType;
      if (source === "logistics") return "TRANSPORT,TOUR_GUIDE";
      return null;
    },
    normalizeSupplierKeyword(keyword) {
      return (keyword || "").trim();
    },
    normalizeSupplierListResponse(response, fallbackLimit) {
      if (Array.isArray(response)) {
        return {
          suppliers: response,
          pagination: {
            page: 1,
            limit: fallbackLimit,
            total: response.length,
            has_more: false,
          },
        };
      }

      return {
        suppliers: response?.data || response?.suppliers || [],
        pagination: response?.pagination || {
          page: 1,
          limit: fallbackLimit,
          total: response?.data?.length || 0,
          has_more: false,
        },
      };
    },
    mergeSupplierListIntoQuoteData(supplierList) {
      const updatedSupplierProductData = { ...(this.supplierProductData || {}) };

      supplierList.forEach((supplier) => {
        const existingEntry = updatedSupplierProductData[supplier.id] || {};
        updatedSupplierProductData[supplier.id] = {
          ...existingEntry,
          supplier,
          products: existingEntry.products || {},
        };
      });

      this.$emit("update:supplier-product-data", updatedSupplierProductData);
    },
    async fetchSupplierList({ contextKey, productType, source, keyword = "", page = 1, append = false }) {
      const normalizedKeyword = this.normalizeSupplierKeyword(keyword);
      const mode = normalizedKeyword ? "search" : "initial";
      const limit = mode === "search" ? 25 : 20;
      const type = this.getSupplierTypeFilter(productType, source);
      const requestKey = `${contextKey}:${mode}:${type || "all"}:${normalizedKeyword}:${page}`;
      const previousState = this.supplierDropdownState[contextKey] || this.getDefaultSupplierDropdownState();

      this.updateSupplierDropdownState(contextKey, {
        keyword: normalizedKeyword,
        type,
        source,
        page: append ? previousState.page : 0,
        limit,
        mode,
        loading: true,
        requestKey,
      });

      try {
        const response = await this.$store.dispatch("SupplierStore/getSuppliersWithProductRateTypes", {
          keyword: normalizedKeyword || undefined,
          page,
          limit,
          type: type || undefined,
          mode,
        });
        const normalizedResponse = this.normalizeSupplierListResponse(response, limit);
        const supplierList = normalizedResponse.suppliers || [];
        const pagination = normalizedResponse.pagination || {};

        this.mergeSupplierListIntoQuoteData(supplierList);

        const latestState = this.supplierDropdownState[contextKey];
        if (!latestState || latestState.requestKey !== requestKey) return;

        const nextSupplierIds = append
          ? [...new Set([...(latestState.supplierIds || []), ...supplierList.map((supplier) => supplier.id)])]
          : supplierList.map((supplier) => supplier.id);

        this.updateSupplierDropdownState(contextKey, {
          supplierIds: nextSupplierIds,
          page: pagination.page || page,
          limit: pagination.limit || limit,
          hasMore:
            pagination.has_more != null ? pagination.has_more : supplierList.length >= (pagination.limit || limit),
          loading: false,
        });
      } catch (error) {
        const latestState = this.supplierDropdownState[contextKey];
        if (latestState?.requestKey === requestKey) {
          this.updateSupplierDropdownState(contextKey, {
            loading: false,
          });
        }
        throw error;
      }
    },
    onSupplierFocus({ contextKey, productType, source }) {
      const dropdownState = this.supplierDropdownState[contextKey];
      if (dropdownState?.loading) return;
      if ((dropdownState?.supplierIds || []).length > 0 && dropdownState.keyword === "") return;

      this.fetchSupplierList({
        contextKey,
        productType,
        source,
        keyword: dropdownState?.keyword || "",
        page: 1,
      }).catch((error) => this.$root.$error(error));
    },
    onSupplierSearch({ contextKey, productType, keyword, source }) {
      this.clearSupplierSearchTimer(contextKey);

      this.supplierSearchTimers[contextKey] = setTimeout(() => {
        this.clearSupplierSearchTimer(contextKey);
        this.fetchSupplierList({
          contextKey,
          productType,
          source,
          keyword,
          page: 1,
        }).catch((error) => this.$root.$error(error));
      }, 300);
    },
    onSupplierLoadMore({ contextKey, productType, source }) {
      const dropdownState = this.supplierDropdownState[contextKey] || this.getDefaultSupplierDropdownState();
      if (dropdownState.loading || !dropdownState.hasMore) return;

      this.fetchSupplierList({
        contextKey,
        productType,
        source,
        keyword: dropdownState.keyword,
        page: (dropdownState.page || 1) + 1,
        append: true,
      }).catch((error) => this.$root.$error(error));
    },
    fetchProductListBySupplierId(supplierId) {
      if (!supplierId) return;

      const existingSupplierData = this.supplierProductData[supplierId];
      const supplier = existingSupplierData?.supplier;
      if (!supplier) return;

      const isLoaded = this.loadedProductSupplierIds.includes(supplierId);
      if (isLoaded) return;

      this.$store.dispatch("ProductStore/getSupplierProductList", supplierId).then((productList) => {
        const products = {};
        productList.forEach((product) => this.$set(products, product.id, product));

        const updatedSupplierProductData = { ...this.supplierProductData };
        updatedSupplierProductData[supplierId] = {
          ...updatedSupplierProductData[supplierId],
          products: products,
        };

        this.$emit("update:supplier-product-data", updatedSupplierProductData);

        this.loadedProductSupplierIds.push(supplierId);
      });
    },
    submitQuote(quoteId, isDuplicated) {
      const workingEnv = this.data.environment || this.$store.getters["AccountStore/environment"];
      if (isDuplicated) {
        this.data.reference = this.data.reference + " - Copy";
      }
      try {
        const body = mapFormDataToBody(this.data, workingEnv);
        if (quoteId) {
          return this.$store.dispatch("QuoteStore/updateQuote", workingEnv);
        }
        return this.$store.dispatch("QuoteStore/createQuote", body);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    submitAndGenerateBooking() {
      this.$root.$loading.open("Generating a booking...");
      const quoteId = this.$route.params.quote_id;
      return this.submitQuote(quoteId, false)
        .then((_) => this.$store.dispatch(`QuoteStore/convertQuoteToBooking`, quoteId))
        .then((bookingId) => {
          const { startYear, startMonth, startDay } = this.data.period;
          const today = new Date();

          let futureDateObj = new Date(today);

          if (startYear && startMonth && startDay) {
            const startDate = new Date(Number(startYear), Number(startMonth) - 1, Number(startDay));

            const sixtyDaysPrior = new Date(startDate);
            sixtyDaysPrior.setDate(startDate.getDate() - 60);

            if (sixtyDaysPrior > today) {
              futureDateObj = sixtyDaysPrior;
            }
          }

          const depositReminderDateObj = new Date(today);
          depositReminderDateObj.setDate(today.getDate() + 60);

          const futureDate = this.formatDate(futureDateObj);
          const depositReminderDate = this.formatDate(depositReminderDateObj);

          const query = [
            this.sendReminder(bookingId, futureDate, "Send Invoice"),
            this.sendReminder(bookingId, futureDate, "Send Rooming List"),
            this.sendReminder(bookingId, futureDate, "Cancellation Deadline"),
            this.sendReminder(bookingId, depositReminderDate, "Deposit Reminder"),
          ];

          return Promise.all(query).then(() => bookingId);
        })
        .then((bookingId) => {
          this.$root.$success("Successfully converted to booking.");
          this.$router.push({ name: "bookings_view", params: { booking_id: bookingId } });
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.$root.$loading.end();
        });
    },
    validateBeforeSave() {
      if (this.isComparisonPreviewMode && this.comparisonPreviewData) {
        this.syncCostOverridesFromPreview(this.comparisonPreviewData);
      }

      const issues = getQuoteValidationIssues(this.data);
      this.validationIssues = issues;
      this.showValidationErrors = issues.length > 0;
      this.$store.commit("QuoteStore/SET_VALIDATION_ISSUES", issues);

      if (!issues.length) {
        this.showValidationErrors = false;
        this.$store.commit("QuoteStore/SET_VALIDATION_ISSUES", []);
        return true;
      }

      this.$root.$error(formatQuoteValidationMessage(issues));
      this.scrollToFirstValidationIssue(issues[0]);
      return false;
    },
    scrollToFirstValidationIssue(issue) {
      this.$nextTick(() => {
        const target = document.getElementById(issue?.anchor || `quote-section-${issue?.section}`);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    validateSupplierSettingUpdated() {
      if (!this.validateBeforeSave()) return;

      if (
        quoteUtils.isSupplierMatchingEnabled(this.data.costs, this.data.logisticsCosts) &&
        this.isSupplierSettingUpdated
      ) {
        this.isSubmitting = true;
        this.openSupplierSettingDialog();
        return;
      }
      return this.submitAndRedirectToQuotes();
    },
    submitAndRedirectToQuotes() {
      this.$root.$loading.open("Updating a quote...");
      const quoteId = this.$route.params.quote_id;
      const successMessage = quoteId ? "Update Quote" : "Created Quote";
      return this.submitQuote(quoteId, false)
        .then(() => {
          this.isSupplierSettingUpdated = false;
          this.showValidationErrors = false;
          this.validationIssues = [];
          this.$store.commit("QuoteStore/SET_VALIDATION_ISSUES", []);
          this.$root.$success(successMessage);
          return quoteId ? this.$store.dispatch("QuoteStore/getOneQuote", quoteId) : null;
        })
        .then((data) => {
          if (data) {
            this.renderQuote(data);
            this.$nextTick(() => this.$emit("saved"));
          } else {
            this.$router.push({ name: "quotes" });
          }
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.isSubmitting = false;
          this.$root.$loading.end();
        });
    },
    sendReminder(bookingId, displayDate, note) {
      return this.$store.dispatch("ReminderStore/addReminder", {
        bookingId: bookingId,
        data: {
          supplier_id: "GENERAL",
          date: displayDate,
          created_by: this.$store.getters["AccountStore/id"],
          note: note,
        },
      });
    },
    submitAndExportOverviewToExcel() {
      this.$root.$loading.open("Exporting to Excel file...");
      const workingEnv = this.$store.getters["AccountStore/environment"];
      const quoteId = this.$route.params.quote_id;
      return this.submitQuote(quoteId, false)
        .then(() => this.$store.dispatch("QuoteStore/getOneQuote", quoteId))
        .then((data) => {
          return quoteUtils
            .exportOverviewToExcel(workingEnv, this.supplierProductData, data, this.marginPercentage)
            .then(() => {
              this.renderQuote(data);
              this.$root.$success("Successfully exported to file.");
            });
        })
        .catch((err) => this.$root.$error(err))
        .finally(() => this.$root.$loading.end());
    },
    submitAndExportDetailToExcel(payload) {
      this.$root.$loading.open("Exporting to Excel file...");
      const quoteId = this.$route.params.quote_id;
      const pax = typeof payload === "object" ? payload?.pax : payload;
      const periodKey = typeof payload === "object" ? payload?.periodKey || "primary" : "primary";
      return this.submitQuote(quoteId, false)
        .then(() => this.$store.dispatch("QuoteStore/getOneQuote", quoteId))
        .then((data) => {
          quoteUtils.exportDetailToExcel(
            this.$store.getters["AccountStore/environment"],
            this.supplierProductData,
            data,
            pax,
            periodKey
          );
          this.renderQuote(data);
          this.$root.$success("Successfully exported to file.");
        })
        .catch((err) => this.$root.$error(err))
        .finally(() => this.$root.$loading.end());
    },
    overviewOptions() {
      this.isOverviewOptionDialogShowed = true;
    },
    programContent() {
      this.isProgramContentDialogShowed = true;
    },
    discountMargin() {
      this.isDiscountMarginDialogShowed = true;
    },
    notes() {
      this.isNotesDialogShowed = true;
    },
    submitAsNewQuote() {
      this.$root.$loading.open("Duplicating a quote...");
      const quoteId = this.$route.params.quote_id;
      return this.submitQuote(quoteId, false)
        .then((_) => {
          this.data.version = Number(this.data.version) + 1;
          this.data.logisticsCosts.forEach((logisticsCost) => {
            const newUUID = quoteUtils.generateUUID();

            this.data.costs.forEach((cost) => {
              cost.items.forEach((item, index) => {
                item.uuid = quoteUtils.generateUUID();
                const uuidIndex = item.logisticsCostUUIDs?.indexOf(logisticsCost.uuid);
                if (uuidIndex >= 0) {
                  item.logisticsCostUUIDs[uuidIndex] = newUUID;
                }
              });
            });

            logisticsCost.uuid = newUUID;
          });

          return this.submitQuote(null, true);
        })
        .then((newId) => this.$store.dispatch("QuoteStore/getOneQuote", newId))
        .then((data) => {
          this.$root.$success("Successfully duplicated a quote");
          this.$router.push({ name: "quotes_view", params: { quote_id: data.quote.id } });
        })
        .catch((err) => this.$root.$error(err))
        .finally(() => this.$root.$loading.end());
    },
    deleteQuote() {
      const quoteId = this.$route.params.quote_id;
      if (!quoteId) return;

      this.$root.$loading.open("Deleting a quote...");
      return this.$store
        .dispatch(`QuoteStore/deleteQuote`, quoteId)
        .then((v) => {
          this.$root.$loading.end();
          this.$root.$success("Successfully deleted Quote");
          this.$router.push({ name: "quotes" });
        })
        .catch((err) => this.$root.$error(err));
    },
    isSelectedPeriodPreviewValid(periodPreview) {
      if (!periodPreview || periodPreview === "primary") return true;

      const prefix = "comparison-";
      if (!String(periodPreview).startsWith(prefix)) return false;

      const index = Number(String(periodPreview).slice(prefix.length));
      if (Number.isNaN(index)) return false;

      const comparisonPeriods = (this.data.comparisonPeriods || []).slice(
        0,
        quoteUtils.MAX_COMPARISON_PERIODS
      );
      return index >= 0 && index < comparisonPeriods.length;
    },
    renderQuote(quote) {
      const previousSelectedPeriodPreview = this.selectedPeriodPreview;
      this.data = mapQuoteResponseToFormData(this.supplierProductData, quote);

      if (this.isSelectedPeriodPreviewValid(previousSelectedPeriodPreview)) {
        this.selectedPeriodPreview = previousSelectedPeriodPreview;
      } else {
        this.selectedPeriodPreview = "primary";
      }

      if (this.selectedPeriodPreview !== "primary") {
        this.refreshComparisonMarginPreview();
      }
    },
    partialUpdateQuote(quote) {
      const quoteId = this.$route.params.quote_id;
      if (!quoteId) return;

      this.$store
        .dispatch("QuoteStore/patchQuote", { quote_id: quoteId, quote: quote })
        .then(() => this.$root.$success("Updated successfully"))
        .catch((err) => this.$root.$error(err));
    },
    openSupplierSettingDialog() {
      this.isSupplierSettingDialogShowed = true;
    },
    updateData(data) {
      this.data = { ...data };
      if (this.isSubmitting) {
        this.submitAndRedirectToQuotes();
      }
    },
    onCostProductTypeChanged(productType) {
      this.onProductTypeChanged(productType, ["ROOM", "MEAL", "EXCURSION"]);
    },
    onLogisticsCostProductTypeChanged(productType) {
      this.onProductTypeChanged(productType, ["TOUR", "TRANS"]);
    },
    onProductTypeChanged(productType, allowedTypes) {
      const quoteId = this.$route.params.quote_id;
      if (!quoteId) return;
      if (!allowedTypes.includes(productType)) return;
      this.isSupplierSettingUpdated = true;
    },
  },
  mounted() {
    const issues = this.$store.getters["QuoteStore/validationIssues"];
    if (issues?.length) {
      this.validationIssues = issues;
      this.showValidationErrors = true;
    }
  },
  beforeDestroy() {
    Object.keys(this.supplierSearchTimers).forEach((contextKey) => this.clearSupplierSearchTimer(contextKey));
  },
};
</script>

<style scoped>
.period-price-banner {
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background: #f5f9ff;
  border: 1px solid #bbdefb;
}

.period-price-banner__content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.period-price-banner__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #37474f;
}

.period-price-banner__active {
  font-weight: 600;
}

.period-price-banner__hint {
  margin-top: 0.75rem;
  color: #ef6c00;
}

.quote-validation-list li {
  margin-bottom: 0.25rem;
}
</style>
